/** Tiny silent MP3 — unlocks HTMLAudio on iOS Safari (SO / iOS14 pattern) */
const SILENT_MP3 =
  'data:audio/mpeg;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV'

let sharedCtx = null
let muted = false
const muteListeners = new Set()

function notifyMute() {
  muteListeners.forEach((fn) => {
    try {
      fn(muted)
    } catch {
      /* ignore */
    }
  })
}

export function onMuteChange(fn) {
  muteListeners.add(fn)
  return () => muteListeners.delete(fn)
}
let unlocked = false
let gestureArmed = false
/** Reused HTMLAudioElement — iOS blocks new Audio().play() without a prior unlock on this same element */
let silentEl = null
let htmlPrimed = false

function AudioContextClass() {
  if (typeof window === 'undefined') return null
  return window.AudioContext || window.webkitAudioContext
}

function getSilentEl() {
  if (silentEl) return silentEl
  silentEl = new Audio()
  silentEl.setAttribute('playsinline', 'true')
  silentEl.preload = 'auto'
  silentEl.loop = false
  silentEl.volume = 0.01
  // autoplay + silent src is the iOS unlock trick from the SO thread
  silentEl.autoplay = true
  silentEl.src = SILENT_MP3
  return silentEl
}

export function getAudioContext() {
  const AC = AudioContextClass()
  if (!AC) throw new Error('Web Audio API not supported')
  if (!sharedCtx) {
    sharedCtx = new AC()
    // Keep context alive when iOS suspends it in the background
    sharedCtx.addEventListener?.('statechange', () => {
      if (sharedCtx?.state === 'suspended' && unlocked && !muted) {
        void sharedCtx.resume()
      }
    })
  }
  return sharedCtx
}

function startBufferSource(source, when = 0) {
  if (typeof source.start === 'function') source.start(when)
  else if (typeof source.noteOn === 'function') source.noteOn(when)
}

/** Synchronous unlock bits — must run inside the user-gesture call stack on iOS */
export function unlockAudioSync() {
  try {
    const el = getSilentEl()
    el.muted = false
    el.volume = 0.01
    if (!htmlPrimed) {
      el.src = SILENT_MP3
      htmlPrimed = true
    }
    const p = el.play()
    if (p && typeof p.catch === 'function') p.catch(() => {})

    const AC = AudioContextClass()
    if (!AC) return false
    const ctx = getAudioContext()
    // Do NOT await — kick resume inside the gesture
    const resumeP = ctx.resume?.()
    if (resumeP && typeof resumeP.catch === 'function') resumeP.catch(() => {})

    try {
      const buffer = ctx.createBuffer(1, 1, 22050)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(ctx.destination)
      startBufferSource(source, 0)
    } catch {
      /* ignore */
    }

    unlocked = true
    return true
  } catch {
    return false
  }
}

/** Resume + HTML silent play + Web Audio silent buffer (iOS Safari safe) */
export async function unlockAudio() {
  unlockAudioSync()
  try {
    const ctx = getAudioContext()
    // Only resume after a real unlock gesture — avoids Chrome autoplay console noise
    if (ctx.state === 'suspended' && unlocked) {
      await ctx.resume().catch(() => {})
    }
    // Second silent buffer after resume settles (Safari sometimes needs this)
    if (ctx.state === 'running') {
      const buffer = ctx.createBuffer(1, 1, ctx.sampleRate || 22050)
      const source = ctx.createBufferSource()
      source.buffer = buffer
      source.connect(ctx.destination)
      startBufferSource(source, 0)
      unlocked = true
    }
    return ctx.state === 'running'
  } catch {
    return unlocked
  }
}

async function ensureAudio() {
  if (muted) return null
  try {
    const ctx = getAudioContext()
    if (ctx.state === 'running') return ctx
    // Do not call resume() outside a user gesture (Chrome blocks + warns)
    if (!unlocked) return null
    await ctx.resume().catch(() => {})
    if (ctx.state !== 'running') return null
    return ctx
  } catch {
    return null
  }
}

/** Call once from the app — arms unlock on every touch/click (required on iPhone) */
export function armAudioGestures() {
  if (gestureArmed || typeof window === 'undefined') return () => {}
  gestureArmed = true

  // Non-passive so we can call unlock synchronously in the gesture
  const arm = () => {
    unlockAudioSync()
  }

  const opts = { capture: true, passive: true }
  window.addEventListener('touchstart', arm, opts)
  window.addEventListener('touchend', arm, opts)
  window.addEventListener('pointerdown', arm, opts)
  window.addEventListener('click', arm, opts)

  const onVis = () => {
    if (document.visibilityState === 'visible') void unlockAudio()
  }
  document.addEventListener('visibilitychange', onVis)
  window.addEventListener('pageshow', onVis)

  // Warm the silent element early (still needs a gesture to actually play)
  try {
    getSilentEl()
  } catch {
    /* ignore */
  }

  return () => {
    window.removeEventListener('touchstart', arm, opts)
    window.removeEventListener('touchend', arm, opts)
    window.removeEventListener('pointerdown', arm, opts)
    window.removeEventListener('click', arm, opts)
    document.removeEventListener('visibilitychange', onVis)
    window.removeEventListener('pageshow', onVis)
    gestureArmed = false
  }
}

export function isAudioUnlocked() {
  return unlocked
}

export function isLikelyIos() {
  if (typeof navigator === 'undefined') return false
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

export function isMuted() {
  return muted
}

export function setMuted(next) {
  muted = Boolean(next)
  if (!muted) unlockAudioSync()
  notifyMute()
  return muted
}

export function toggleMute() {
  muted = !muted
  if (!muted) unlockAudioSync()
  notifyMute()
  return muted
}

function envGain(ctx, volume, attack = 0.01, hold = 0.05, release = 0.08) {
  const gain = ctx.createGain()
  const now = ctx.currentTime
  gain.gain.setValueAtTime(0, now)
  gain.gain.linearRampToValueAtTime(volume, now + attack)
  gain.gain.linearRampToValueAtTime(volume, now + attack + hold)
  gain.gain.linearRampToValueAtTime(0, now + attack + hold + release)
  return { gain, stopAt: now + attack + hold + release + 0.02 }
}

/** Soft UI click */
export async function sfxClick() {
  if (muted) return
  const ctx = await ensureAudio()
  if (!ctx) return
  const osc = ctx.createOscillator()
  const { gain, stopAt } = envGain(ctx, 0.08, 0.005, 0.02, 0.04)
  osc.type = 'triangle'
  osc.frequency.setValueAtTime(520, ctx.currentTime)
  osc.frequency.exponentialRampToValueAtTime(180, ctx.currentTime + 0.06)
  osc.connect(gain)
  gain.connect(ctx.destination)
  startBufferSource(osc)
  osc.stop(stopAt)
}

/** Countdown: ready / set / go */
export async function sfxCountdown(phase) {
  if (muted) return
  const ctx = await ensureAudio()
  if (!ctx) return
  const map = { ready: 440, set: 554, go: 740 }
  const freq = map[phase] || 440
  const osc = ctx.createOscillator()
  const hold = phase === 'go' ? 0.12 : 0.05
  const { gain, stopAt } = envGain(ctx, phase === 'go' ? 0.18 : 0.14, 0.005, hold, 0.08)
  osc.type = 'sine'
  osc.frequency.value = freq
  osc.connect(gain)
  gain.connect(ctx.destination)
  startBufferSource(osc)
  osc.stop(stopAt)
}

/** Submit / lock-in */
export async function sfxSubmit() {
  if (muted) return
  const ctx = await ensureAudio()
  if (!ctx) return
  const now = ctx.currentTime
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(220, now)
  osc.frequency.exponentialRampToValueAtTime(880, now + 0.12)
  gain.gain.setValueAtTime(0, now)
  gain.gain.linearRampToValueAtTime(0.16, now + 0.02)
  gain.gain.linearRampToValueAtTime(0, now + 0.18)
  osc.connect(gain)
  gain.connect(ctx.destination)
  startBufferSource(osc, now)
  osc.stop(now + 0.2)
}

/** Score reveal — brighter when higher */
export async function sfxScore(score = 50) {
  if (muted) return
  const ctx = await ensureAudio()
  if (!ctx) return
  const now = ctx.currentTime
  const base = 300 + (Math.min(100, score) / 10) * 40
  ;[0, 0.08, 0.16].forEach((delay, i) => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'triangle'
    osc.frequency.value = base * (1 + i * 0.25)
    const t = now + delay
    gain.gain.setValueAtTime(0, t)
    gain.gain.linearRampToValueAtTime(0.11, t + 0.02)
    gain.gain.linearRampToValueAtTime(0, t + 0.14)
    osc.connect(gain)
    gain.connect(ctx.destination)
    startBufferSource(osc, t)
    osc.stop(t + 0.16)
  })
}

/** Subtle slider tick while dragging */
let lastTick = 0
export async function sfxTick() {
  if (muted) return
  const now = performance.now()
  if (now - lastTick < 45) return
  lastTick = now
  const ctx = await ensureAudio()
  if (!ctx) return
  const osc = ctx.createOscillator()
  const { gain, stopAt } = envGain(ctx, 0.035, 0.001, 0.01, 0.02)
  osc.type = 'square'
  osc.frequency.value = 1200 + Math.random() * 400
  osc.connect(gain)
  gain.connect(ctx.destination)
  startBufferSource(osc)
  osc.stop(stopAt)
}

/** Next-round / transition blip */
export async function sfxNext() {
  if (muted) return
  const ctx = await ensureAudio()
  if (!ctx) return
  const osc = ctx.createOscillator()
  const { gain, stopAt } = envGain(ctx, 0.1, 0.005, 0.04, 0.06)
  osc.type = 'sine'
  osc.frequency.value = 660
  osc.connect(gain)
  gain.connect(ctx.destination)
  startBufferSource(osc)
  osc.stop(stopAt)
}

/** Soft tile slide for puzzle */
export async function sfxSlide() {
  if (muted) return
  const ctx = await ensureAudio()
  if (!ctx) return
  const now = ctx.currentTime
  const bufferSize = Math.floor(ctx.sampleRate * 0.11)
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < bufferSize; i++) {
    const env = Math.sin((i / bufferSize) * Math.PI)
    data[i] = (Math.random() * 2 - 1) * env * 0.7
  }
  const src = ctx.createBufferSource()
  const filter = ctx.createBiquadFilter()
  const gain = ctx.createGain()
  filter.type = 'bandpass'
  filter.frequency.setValueAtTime(1100, now)
  filter.frequency.exponentialRampToValueAtTime(320, now + 0.1)
  filter.Q.value = 1.1
  gain.gain.setValueAtTime(0.18, now)
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.11)
  src.buffer = buffer
  src.connect(filter)
  filter.connect(gain)
  gain.connect(ctx.destination)
  startBufferSource(src, now)

  const thud = ctx.createOscillator()
  const thudGain = ctx.createGain()
  thud.type = 'sine'
  thud.frequency.setValueAtTime(180, now)
  thud.frequency.exponentialRampToValueAtTime(90, now + 0.08)
  thudGain.gain.setValueAtTime(0.08, now)
  thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.09)
  thud.connect(thudGain)
  thudGain.connect(ctx.destination)
  startBufferSource(thud, now)
  thud.stop(now + 0.1)
}

/** Crisp snap when a tile locks into place */
export async function sfxSnap() {
  if (muted) return
  const ctx = await ensureAudio()
  if (!ctx) return
  const now = ctx.currentTime
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'triangle'
  osc.frequency.setValueAtTime(640, now)
  osc.frequency.exponentialRampToValueAtTime(280, now + 0.06)
  gain.gain.setValueAtTime(0, now)
  gain.gain.linearRampToValueAtTime(0.14, now + 0.004)
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08)
  osc.connect(gain)
  gain.connect(ctx.destination)
  startBufferSource(osc, now)
  osc.stop(now + 0.09)
}

/** Soft whoosh when color stimulus appears */
export async function sfxWhoosh() {
  if (muted) return
  const ctx = await ensureAudio()
  if (!ctx) return
  const now = ctx.currentTime
  const bufferSize = Math.floor(ctx.sampleRate * 0.25)
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
  const data = buffer.getChannelData(0)
  for (let i = 0; i < bufferSize; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize)
  const src = ctx.createBufferSource()
  const filter = ctx.createBiquadFilter()
  const gain = ctx.createGain()
  filter.type = 'lowpass'
  filter.frequency.setValueAtTime(400, now)
  filter.frequency.exponentialRampToValueAtTime(2000, now + 0.2)
  gain.gain.setValueAtTime(0.12, now)
  gain.gain.linearRampToValueAtTime(0, now + 0.25)
  src.buffer = buffer
  src.connect(filter)
  filter.connect(gain)
  gain.connect(ctx.destination)
  startBufferSource(src, now)
}

export async function playTone(freq, durationMs = 1200, volume = 0.24) {
  if (muted) return
  const ctx = await ensureAudio()
  if (!ctx) return
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.value = freq
  osc.connect(gain)
  gain.connect(ctx.destination)
  const now = ctx.currentTime
  const dur = durationMs / 1000
  gain.gain.setValueAtTime(0, now)
  gain.gain.linearRampToValueAtTime(volume, now + 0.04)
  gain.gain.linearRampToValueAtTime(volume, now + Math.max(0.05, dur - 0.08))
  gain.gain.linearRampToValueAtTime(0, now + dur)
  startBufferSource(osc, now)
  osc.stop(now + dur + 0.02)
}

/** Continuous tone for sound-game dragging */
let liveOsc = null
let liveGain = null

export async function startLiveTone(freq = 440, volume = 0.16) {
  if (muted) return
  const ctx = await ensureAudio()
  if (!ctx) return
  stopLiveTone()
  liveOsc = ctx.createOscillator()
  liveGain = ctx.createGain()
  liveOsc.type = 'sine'
  liveOsc.frequency.value = freq
  liveGain.gain.value = volume
  liveOsc.connect(liveGain)
  liveGain.connect(ctx.destination)
  startBufferSource(liveOsc)
}

export function updateLiveTone(freq) {
  if (!liveOsc || muted) return
  try {
    liveOsc.frequency.setTargetAtTime(freq, getAudioContext().currentTime, 0.01)
  } catch {
    /* ignore */
  }
}

export function stopLiveTone() {
  try {
    liveOsc?.stop()
  } catch {
    /* already stopped */
  }
  liveOsc = null
  liveGain = null
}

export function randomFrequency(min = 220, max = 880) {
  const lo = Math.log(min)
  const hi = Math.log(max)
  return Math.exp(lo + Math.random() * (hi - lo))
}

export function scorePitch(targetHz, guessHz) {
  const cents = Math.abs(1200 * Math.log2(guessHz / targetHz))
  return Math.max(0, Math.min(100, 100 * Math.pow(1 - Math.min(1, cents / 600), 1.2)))
}
